<template>
  <b-modal
    v-model="isModalVisible"
    size="xl"
    dialog-class="mt-md-5 p-0"
    content-class="p-2"
    footer-class="border-top-0"
    header-class="border-bottom-0"
    @ok="submit"
    @cancel="cancel"
  >
    <template v-slot:modal-header>
      <h5>{{ $t("previewModal.usersToCreate") }}: {{ userList.length }}</h5>
    </template>
    
    <template v-slot:default>
      <div v-if="userList.length > 0">
        <!-- Subscription Info -->
        <b-card class="mb-3" v-if="selectedService">
          <b-row class="align-items-center">
            <b-col>
              <strong>{{ $t("previewModal.selectedService") }}:</strong> {{ selectedService.name }}
            </b-col>
            <b-col>
              <strong>{{ $t("previewModal.numAnalysis") }}:</strong> 
              {{ selectedService.numAnalysis !== 0 ? selectedService.numAnalysis : "Unlimited" }}
            </b-col>
            <b-col v-if="selectedService.numDaysActive">
              <strong>{{ $t("previewModal.validity") }}:</strong> 
              {{ selectedService.numDaysActive }} {{ $t("previewModal.days") }}
            </b-col>
          </b-row>
        </b-card>

        <!-- Toggle Button -->
        <b-row class="mb-2">
          <b-col>
            <b-button 
              size="sm" 
              variant="outline-primary"
              @click="showAllFields = !showAllFields"
            >
              <i :class="showAllFields ? 'fa fa-eye-slash' : 'fa fa-eye'"></i>
              {{ showAllFields ? $t("previewModal.hideFields") : $t("previewModal.showAllFields") }}
            </b-button>
          </b-col>
        </b-row>

        <b-table
          :items="userList"
          :fields="visibleFields"
          responsive
          striped
          hover
          sticky-header="500px"
        >
          <template #cell(email)="data">
            <b-form-input
              v-model="data.item.email"
              size="sm"
              :state="isValidEmail(data.item.email)"
            />
          </template>
          
          <template #cell(firstName)="data">
            <b-form-input
              v-model="data.item.firstName"
              size="sm"
            />
          </template>
          
          <template #cell(lastName)="data">
            <b-form-input
              v-model="data.item.lastName"
              size="sm"
            />
          </template>
          
          <template #cell(companyName)="data">
            <b-form-input
              v-model="data.item.companyName"
              size="sm"
            />
          </template>

          <template #cell(country)="data">
            <b-form-input
              v-model="data.item.country"
              size="sm"
            />
          </template>

          <template #cell(phone)="data">
            <b-form-input
              v-model="data.item.phone"
              size="sm"
            />
          </template>

          <template #cell(jobTitle)="data">
            <b-form-input
              v-model="data.item.jobTitle"
              size="sm"
            />
          </template>

          <template #cell(address)="data">
            <b-form-input
              v-model="data.item.address"
              size="sm"
            />
          </template>

          <template #cell(city)="data">
            <b-form-input
              v-model="data.item.city"
              size="sm"
            />
          </template>

          <template #cell(province)="data">
            <b-form-input
              v-model="data.item.province"
              size="sm"
            />
          </template>

          <template #cell(zip)="data">
            <b-form-input
              v-model="data.item.zip"
              size="sm"
            />
          </template>

          <template #cell(vat)="data">
            <b-form-input
              v-model="data.item.vat"
              size="sm"
            />
          </template>

          <template #cell(clientType)="data">
            <b-form-select
              v-model="data.item.clientType"
              :options="clientTypeOptions"
              size="sm"
            />
          </template>

          <template #cell(externalProvider)="data">
            <span class="text-muted">{{ data.item.externalProvider }}</span>
          </template>
          
          <template #cell(actions)="data">
            <b-button
              variant="danger"
              size="sm"
              @click="removeUser(data.index)"
            >
              <img
                src="../../assets/general-icons/trash.svg"
                class="delete__icon clickable"
              />
            </b-button>
          </template>
        </b-table>
      </div>
      
      <div v-else class="text-center p-5">
        <h5>{{ $t("previewModal.noUsers") }}</h5>
      </div>
    </template>
    
    <template v-slot:modal-footer="{ ok, cancel }">
      <b-button
        variant="white"
        class="mr-3 btn-blue-border justify-content-center align-items-center bold"
        @click="cancel"
      >
        {{ $t("button.cancel") }}
      </b-button>
      <b-button
        variant="dark"
        class="justify-content-center align-items-center bold"
        :disabled="!hasValidUsers"
        @click="ok"
      >
        {{ $t("button.createUsers") }}
      </b-button>
    </template>
  </b-modal>
</template>

<script>
export default {
  props: {
    modalStatus: {
      type: Boolean,
      required: true
    },
    users: {
      type: Array,
      required: true
    }
  },
  data() {
    return {
      userList: [],
      showAllFields: false,
      basicFields: [
        { key: 'email', label: 'Email', sortable: true },
        { key: 'firstName', label: 'First Name', sortable: true },
        { key: 'lastName', label: 'Last Name', sortable: true },
        { key: 'companyName', label: 'Company', sortable: true },
        { key: 'externalProvider', label: 'Provider', sortable: true },
        { key: 'actions', label: 'Actions' }
      ],
      allFields: [
        { key: 'email', label: 'Email', sortable: true },
        { key: 'firstName', label: 'First Name', sortable: true },
        { key: 'lastName', label: 'Last Name', sortable: true },
        { key: 'companyName', label: 'Company', sortable: true },
        { key: 'country', label: 'Country', sortable: true },
        { key: 'phone', label: 'Phone', sortable: true },
        { key: 'jobTitle', label: 'Job Title', sortable: true },
        { key: 'address', label: 'Address', sortable: true },
        { key: 'city', label: 'City', sortable: true },
        { key: 'province', label: 'Province', sortable: true },
        { key: 'zip', label: 'ZIP', sortable: true },
        { key: 'vat', label: 'VAT', sortable: true },
        // { key: 'clientType', label: 'Type', sortable: true },
        { key: 'externalProvider', label: 'Provider', sortable: true },
        { key: 'actions', label: 'Actions' }
      ],
      clientTypeOptions: [
        // { value: 'agency', text: 'Agency' },
        // { value: 'custom', text: 'Custom' },
        // { value: 'regular', text: 'Regular' },
        // { value: 'pharmaceutical', text: 'Pharmaceutical' },
        { value: 'predict', text: 'Predict (Foresight)' }
      ]
    };
  },
  watch: {
    users: {
      immediate: true,
      handler(newUsers) {
        this.userList = JSON.parse(JSON.stringify(newUsers));
      }
    }
  },
  computed: {
    isModalVisible: {
      get() {
        return this.modalStatus;
      },
      set(modalStatus) {
        this.$emit("setModalStatus", modalStatus);
      }
    },
    hasValidUsers() {
      return this.userList.length > 0 && 
             this.userList.every(user => this.isValidEmail(user.email));
    },
    selectedService() {
      // Get service info from the first user's serviceId
      if (this.userList.length === 0 || !this.userList[0].serviceId) {
        return null;
      }
      
      const serviceId = this.userList[0].serviceId;
      const serviceList = this.$store.getters["servicePredict/getServiceList"];
      return serviceList.find(s => s._id === serviceId);
    },
    visibleFields() {
      return this.showAllFields ? this.allFields : this.basicFields;
    }
  },
  methods: {
    isValidEmail(email) {
      const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return regex.test(email);
    },
    removeUser(index) {
      this.userList.splice(index, 1);
    },
    submit() {
      this.$emit("submit", this.userList);
    },
    cancel() {
      this.userList = JSON.parse(JSON.stringify(this.users));
      this.showAllFields = false;
    }
  }
};
</script>

<style scoped>
.btn-blue-border {
  border: 1px solid #007bff;
  color: #007bff;
}
.delete__icon {
  width: 16px;
  filter: invert(49%) sepia(7%) saturate(539%) hue-rotate(167deg)
    brightness(90%) contrast(88%);
}
</style>